import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  SecurityContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { SkillsImportDataFacade } from '@core/tellent-skills-logic';
import type {
  SkillsImport,
  SkillsImportMessage,
  SkillsImportMessageType,
  SkillsImportProficiency,
  SkillsImportSkill,
} from '@core/tellent-skills-types';
import { ImportedSkillCardComponent } from '@core/tellent-skills-ui';
import { StepperComponent, StepperStep } from '@core/tellent-ui';
import {
  AiLogoComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  type IconName,
  type IconToken,
  NotificationsController,
  RadioComponent,
} from '@core/user-interface-legacy';

type MessageIconConfig = { name: IconName; colorToken: IconToken };
type SanitizedMessage = SkillsImportMessage & { sanitizedText: string };

const MESSAGE_ICON_CONFIG: Record<SkillsImportMessageType, MessageIconConfig> = {
  positive: { name: 'check', colorToken: 'colorIconSuccessDefault' },
  info: { name: 'info-outline', colorToken: 'colorIconInfoDefault' },
  warning: { name: 'warning', colorToken: 'colorIconWarningDefault' },
  blocking_error: { name: 'error', colorToken: 'colorIconCriticalDefault' },
};

@Component({
  selector: 'tlc-import-skills-summary-dialog',
  templateUrl: './import-skills-summary-dialog.component.html',
  styleUrl: './import-skills-summary-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiLogoComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    GroupComponent,
    IconComponent,
    ImportedSkillCardComponent,
    InfoboxComponent,
    RadioComponent,
    StepperComponent,
    TranslatePipe,
    HeadingComponent,
  ],
})
export class ImportSkillsSummaryDialogComponent implements DialogComponentI<
  { id: number },
  Record<string, never>
> {
  private readonly t = injectTranslate();
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly skillsImportDataFacade = inject(SkillsImportDataFacade);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly notificationsController = inject(NotificationsController);

  protected readonly skillsImport = signal<SkillsImport | null>(null);
  protected readonly addNewPositions = signal<boolean>(true);
  protected readonly newPositions = computed(() =>
    (this.skillsImport()?.result?.positions ?? []).filter(p => p.existingPositionGuid === null),
  );
  protected readonly skillsCount = computed(() => this.skillsImport()?.result?.skills.length ?? 0);
  protected readonly positionsCount = computed(
    () => this.skillsImport()?.result?.positions.length ?? 0,
  );
  protected readonly skills = computed(() => this.skillsImport()?.result?.skills ?? []);
  protected readonly groups = computed(() => this.skillsImport()?.result?.groups ?? []);
  protected readonly positions = computed(() => this.skillsImport()?.result?.positions ?? []);
  protected readonly messages = computed(() => this.skillsImport()?.result?.messages ?? []);
  protected readonly sanitizedMessages = computed<SanitizedMessage[]>(() =>
    this.messages().map(message => ({
      ...message,
      sanitizedText: this.sanitizer.sanitize(SecurityContext.HTML, message.text) ?? '',
    })),
  );
  protected readonly positiveAndInfoMessages = computed(() =>
    this.sanitizedMessages().filter(
      message => message.type === 'positive' || message.type === 'info',
    ),
  );
  protected readonly warningMessages = computed(() =>
    this.sanitizedMessages().filter(message => message.type === 'warning'),
  );
  protected readonly blockingErrorMessages = computed(() =>
    this.messages().filter(m => m.type === 'blocking_error'),
  );
  protected readonly messageIconConfig = MESSAGE_ICON_CONFIG;

  protected readonly steps: StepperStep[] = [
    {
      id: 'upload_file',
      label: this.t('tlc.positions.import_skills_flow.stepper.upload_file'),
      active: false,
      completed: true,
    },
    {
      id: 'preview_skills',
      label: this.t('tlc.positions.import_skills_flow.stepper.preview_skills'),
      active: true,
      completed: false,
    },
    {
      id: 'apply_changes',
      label: this.t('tlc.positions.import_skills_flow.stepper.apply_changes'),
      active: false,
      completed: false,
    },
  ];

  public setDialogData(data: { id: number }, _triggerUpdate: boolean): void {
    this.skillsImportDataFacade
      .fetchImport(data.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: skillsImport => this.skillsImport.set(skillsImport),
        error: () =>
          this.notificationsController.error({ title: this.t('tlc.common.errors.unknown_error') }),
      });
  }

  public close(): void {
    this.dialogRef.close({});
  }

  public onSkillSave(
    index: number,
    data: Pick<SkillsImportSkill, 'group' | 'name' | 'description'>,
  ): void {
    this.skillsImport.update(current => {
      if (!current?.result) return current;
      const skills = [...current.result.skills];
      skills[index] = { ...skills[index], ...data };

      return { ...current, result: { ...current.result, skills } };
    });
  }

  public onProficiencySave(
    skillIndex: number,
    event: {
      index: number;
      data: Pick<SkillsImportProficiency, 'name' | 'description' | 'positions'>;
    },
  ): void {
    this.skillsImport.update(current => {
      if (!current?.result) return current;
      const skills = [...current.result.skills];
      const proficiencies = [...skills[skillIndex].proficiencies];
      proficiencies[event.index] = { ...proficiencies[event.index], ...event.data };
      skills[skillIndex] = { ...skills[skillIndex], proficiencies };

      return { ...current, result: { ...current.result, skills } };
    });
  }
}
